import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import CustomerGrowthChart from '../components/CustomerGrowthChart.jsx';

// "Customer History" page.
//   - Sites Installed bar chart (one bar per year, count of all site
//     installs that year). Each bar is clickable -> drill-down modal
//     listing the sites + their parent customers.
//   - Anniversaries This Month tile grid (1/5/10/15/20/25-year
//     milestones since each customer's first site install).
//
// All install dates source from sites.install_date - the customer_details
// .install_date column is a Maximizer import artifact and not the
// source of truth. Per Dave 2026-05-30.

function formatDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

export default function CustomerHistory() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Year currently being drilled into via the modal. null = closed.
  const [yearOpen, setYearOpen] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    axios.get('/api/customer-history')
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => { if (!cancelled) setError(e.response?.data?.error || e.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (yearOpen === null) return;
    const onKey = (e) => { if (e.key === 'Escape') setYearOpen(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [yearOpen]);

  // Derived stats for the header strip - all site-based now since the
  // chart counts sites, not customers.
  const stats = useMemo(() => {
    const sg = (data && data.site_growth) || [];
    if (!sg.length) {
      return { total: 0, thisYear: 0, bestYear: null, bestCount: 0, longest: 0, maxCount: 0 };
    }
    const currentYear = new Date().getFullYear();
    let total = 0, bestCount = 0, bestYear = null, maxCount = 0, thisYear = 0;
    for (const row of sg) {
      total += row.sites;
      if (row.sites > maxCount) maxCount = row.sites;
      if (row.sites > bestCount) { bestCount = row.sites; bestYear = row.year; }
      if (row.year === currentYear) thisYear = row.sites;
    }
    const earliest = sg[0]?.year ?? currentYear;
    return { total, thisYear, bestYear, bestCount, longest: currentYear - earliest, maxCount };
  }, [data]);

  if (loading) {
    return <section className="page"><p>Loading customer history…</p></section>;
  }
  if (error) {
    return (
      <section className="page">
        <div className="page-header"><h2>Customer History</h2></div>
        <p className="error">{error}</p>
      </section>
    );
  }

  const site_growth = data?.site_growth || [];
  const anniversaries = data?.anniversaries || [];
  const currentYear = new Date().getFullYear();

  return (
    <section className="page">
      <div className="page-header">
        <div>
          <h2>Customer History</h2>
          <div className="subtitle">
            How far we've come - sites installed by year and milestone anniversaries.
          </div>
        </div>
      </div>

      {/* New: yearly Customer Growth chart (new vs lost). Slots above
          the existing site-installs / anniversaries split so the growth
          story leads the page. Dave 2026-06-30. */}
      <CustomerGrowthChart />

      {/* 75/25 vertical split. Sites chart on top, anniversaries below. */}
      <div style={{
        display: 'grid',
        gridTemplateRows: '3fr 1fr',
        gap: 'var(--space-4)',
        minHeight: 'calc(100vh - 180px)'
      }}>

        {/* ─── Sites Installed panel ───────────────────────────── */}
        <section className="panel" style={{ display: 'flex', flexDirection: 'column' }}>
          <header className="panel-head">
            <h3>Sites Installed</h3>
            <span className="panel-count">{stats.total}</span>
          </header>

          {site_growth.length === 0 ? (
            <p className="panel-empty">No sites with install dates yet.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
              <div className="growth-stats" style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(4, 1fr)',
                gap: 'var(--space-3)',
                marginBottom: 'var(--space-5)'
              }}>
                <div className="growth-stat">
                  <div className="growth-stat-label">Best year</div>
                  <div className="growth-stat-value">
                    {stats.bestYear ? `${stats.bestYear} · ${stats.bestCount}` : '—'}
                  </div>
                </div>
                <div className="growth-stat">
                  <div className="growth-stat-label">This year</div>
                  <div className="growth-stat-value">{stats.thisYear}</div>
                </div>
                <div className="growth-stat">
                  <div className="growth-stat-label">Total sites</div>
                  <div className="growth-stat-value">{stats.total}</div>
                </div>
                <div className="growth-stat">
                  <div className="growth-stat-label">Years in business</div>
                  <div className="growth-stat-value">{stats.longest}</div>
                </div>
              </div>

              {/* Bar chart. Best year = gold + star, current year = teal,
                  others = teal gradient. All bars clickable. */}
              <div className="growth-chart" style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${site_growth.length}, 1fr)`,
                gap: 'var(--space-2)',
                alignItems: 'end',
                flex: 1,
                minHeight: 200,
                padding: '28px 0 0',
                borderBottom: '1px solid var(--color-border)'
              }}>
                {site_growth.map((row) => {
                  const isCurrent = row.year === currentYear;
                  const isBest = row.year === stats.bestYear && stats.bestCount > 0;
                  const pct = stats.maxCount
                    ? Math.max(4, Math.round((row.sites / stats.maxCount) * 100))
                    : 0;

                  let background, boxShadow, labelColor;
                  if (isBest) {
                    background = 'linear-gradient(180deg, #fbbf24 0%, #d97706 100%)';
                    boxShadow = '0 2px 6px rgba(217, 119, 6, 0.35)';
                    labelColor = '#92400e';
                  } else if (isCurrent) {
                    background = 'linear-gradient(180deg, var(--color-accent) 0%, var(--color-accent-dark) 100%)';
                    boxShadow = '0 2px 6px rgba(14, 116, 144, 0.30)';
                    labelColor = 'var(--color-accent-dark)';
                  } else {
                    background = 'linear-gradient(180deg, var(--color-accent-soft) 0%, var(--color-accent) 100%)';
                    boxShadow = '0 1px 3px rgba(14, 116, 144, 0.15)';
                    labelColor = 'var(--color-text)';
                  }

                  return (
                    <div key={row.year} style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      height: '100%',
                      gap: 'var(--space-1)'
                    }}>
                      <div style={{ flex: 1, display: 'flex', alignItems: 'end', width: '100%', position: 'relative' }}>
                        <div
                          title={`${row.year}: ${row.sites} site${row.sites === 1 ? '' : 's'}${isBest ? ' (best year)' : ''} - click to see which`}
                          onClick={() => setYearOpen(row.year)}
                          onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-3px)'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
                          style={{
                            width: '100%',
                            height: `${pct}%`,
                            background,
                            boxShadow,
                            borderRadius: 'var(--radius-sm) var(--radius-sm) 0 0',
                            position: 'relative',
                            cursor: 'pointer',
                            transition: 'transform 0.15s ease'
                          }}
                        >
                          {isBest && (
                            <span style={{
                              position: 'absolute',
                              top: -38,
                              left: 0,
                              right: 0,
                              textAlign: 'center',
                              fontSize: 14,
                              filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.15))'
                            }}>★</span>
                          )}
                          <span style={{
                            position: 'absolute',
                            top: -22,
                            left: 0,
                            right: 0,
                            textAlign: 'center',
                            fontSize: 'var(--text-body)',
                            fontFamily: 'var(--font-mono)',
                            color: labelColor,
                            fontWeight: (isCurrent || isBest) ? 'var(--font-semibold)' : 'var(--font-regular)'
                          }}>{row.sites}</span>
                        </div>
                      </div>
                      <span className="cell-mono" style={{
                        fontSize: 'var(--text-small)',
                        color: isCurrent ? 'var(--color-accent-dark)' : isBest ? '#92400e' : 'var(--color-text-dim)',
                        fontWeight: (isCurrent || isBest) ? 'var(--font-semibold)' : 'var(--font-regular)'
                      }}>{row.year}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </section>

        {/* ─── Anniversaries panel ─────────────────────────────── */}
        <section className="panel" style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <header className="panel-head">
            <h3>Anniversaries This Month</h3>
            <span className="panel-count">{anniversaries.length}</span>
          </header>

          {anniversaries.length === 0 ? (
            <p className="panel-empty">No milestone anniversaries this month. 🎂</p>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: 'var(--space-2)',
              overflowY: 'auto',
              flex: 1,
              alignContent: 'start'
            }}>
              {anniversaries.map((a) => {
                const isBig = a.years >= 10;
                const isMid = a.years >= 5;
                const dateLabel = formatDate(a.install_date);
                return (
                  <div
                    key={a.id}
                    onClick={() => navigate(`/customers/${a.id}`)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-2)',
                      padding: 'var(--space-2) var(--space-3)',
                      background: 'var(--color-surface-sunken)',
                      borderRadius: 'var(--radius-md)',
                      cursor: 'pointer',
                      minHeight: 48
                    }}
                  >
                    <div style={{
                      fontSize: 'var(--text-large)',
                      fontWeight: 'var(--font-semibold)',
                      fontFamily: 'var(--font-mono)',
                      textAlign: 'center',
                      minWidth: 36,
                      color: isBig ? 'var(--color-good)' : isMid ? 'var(--color-accent)' : 'var(--color-warn)'
                    }}>{a.years}y</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontWeight: 'var(--font-semibold)',
                        fontSize: 'var(--text-body)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {a.company || [a.first_name, a.last_name].filter(Boolean).join(' ') || `Customer #${a.id}`}
                      </div>
                      <div style={{
                        fontSize: 'var(--text-small)',
                        color: 'var(--color-text-dim)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        {dateLabel} · {a.sites_count} site{a.sites_count === 1 ? '' : 's'}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

      </div>

      {/* ─── Year drill-down modal (sites for selected year) ──── */}
      {yearOpen !== null && (() => {
        const sites = (data?.sites_by_year || {})[String(yearOpen)] || [];
        return (
          <div
            className="modal-overlay"
            onMouseDown={(e) => { if (e.target === e.currentTarget) setYearOpen(null); }}
          >
            <div
              className="modal-card"
              role="dialog"
              aria-modal="true"
              aria-label={`Sites installed in ${yearOpen}`}
              style={{ maxWidth: 820, width: '100%' }}
            >
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 'var(--space-4)'
              }}>
                <div>
                  <h3 style={{ margin: 0 }}>Sites installed in {yearOpen}</h3>
                  <div style={{
                    fontSize: 'var(--text-small)',
                    color: 'var(--color-text-dim)',
                    marginTop: 2
                  }}>
                    {sites.length} site{sites.length === 1 ? '' : 's'}
                  </div>
                </div>
                <button
                  type="button"
                  className="btn-text"
                  onClick={() => setYearOpen(null)}
                  aria-label="Close"
                >✕</button>
              </div>

              {sites.length === 0 ? (
                <p className="panel-empty">No sites installed in {yearOpen}.</p>
              ) : (
                <table className="data-table" style={{ width: '100%' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left' }}>Customer</th>
                      <th style={{ textAlign: 'left' }}>Site</th>
                      <th style={{ textAlign: 'left' }}>Location</th>
                      <th style={{ textAlign: 'left' }}>Installed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sites.map((s) => {
                      const name = s.company
                        || [s.first_name, s.last_name].filter(Boolean).join(' ')
                        || `Customer #${s.customer_id}`;
                      const siteLabel = s.site_name
                        || (s.site_number ? `# ${s.site_number}` : `Site #${s.site_id}`);
                      const location = [s.city, s.province_state].filter(Boolean).join(', ');
                      return (
                        <tr
                          key={s.site_id}
                          onClick={() => { setYearOpen(null); navigate(`/customers/${s.customer_id}`); }}
                          style={{ cursor: 'pointer' }}
                        >
                          <td>{name}</td>
                          <td>{siteLabel}</td>
                          <td style={{ color: 'var(--color-text-dim)' }}>{location || '—'}</td>
                          <td className="cell-mono">{formatDate(s.install_date)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        );
      })()}
    </section>
  );
}
